import React, { FC, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';
import { CommonInput, FlexBox, INPUT_TYPES, useToasts } from '@brg/ui';
import styled from 'styled-components';
import { useModal } from 'core';
import {
    CalendarLabel,
    DayButton,
    DaysContainer,
    GroupButton,
    GroupsContainer,
    GroupsRow,
    LessonTypeWrapper,
    StyledDateTimePicker,
    StyledRectangularButtonFilled,
} from 'shared/pages/projectEdit/components/LMS/LessonsEditor/styles';
import {
    ILessonData,
    LMSLessonClassTypeModel,
} from 'shared/pages/projectEdit/components/LMS/LessonsEditor/LessonsEditorTypes';
import { Date24Icon } from 'shared/components/tagAdminProjectsSearchBar/date24';
import CloseIcon from 'shared/icons/CloseIcon';
import ClockIcon16 from 'shared/icons/ClockIcon16';

const MAX_GROUP_BUTTON_LABEL = 15;

interface CourseGroup {
    groupId: string;
    name: Array<{ lang: string; name: string }>;
}

interface LessonTypeOption {
    value: string;
    label: string;
}

interface LessonCreateFormValues {
    isRecurring: boolean;
    courseStartDate: Date | null;
    courseEndDate: Date | null;
    singleDate: Date | null;
    selectedDays: number[];
    start: string;
    end: string;
    selectedLessonType: LMSLessonClassTypeModel;
    location: string;
    selectedGroups: string[];
}

export interface LessonCreateModalProps {
    defaultStartDate?: Date | null;
    defaultEndDate?: Date | null;
    maxDate?: Date | null;
    courseGroups: CourseGroup[];
    lessonTypeOptions: LessonTypeOption[];
    getGroupName: (group: CourseGroup) => string;
    onSubmit: (data: ILessonData, onSuccess?: () => void) => void;
}

const ModalWrapper = styled.div`
    background: #ffffff;
    border-radius: 8px;
    width: 720px;
    max-width: calc(100vw - 32px);
    padding: 32px;
    display: flex;
    flex-direction: column;
    gap: 20px;
    max-height: 90vh;
    overflow-y: auto;
    scrollbar-width: thin;
    scrollbar-color: #c4c4c4 transparent;

    &::-webkit-scrollbar {
        width: 6px;
    }
    &::-webkit-scrollbar-track {
        background: transparent;
    }
    &::-webkit-scrollbar-thumb {
        background-color: #c4c4c4;
        border-radius: 3px;
    }
    &::-webkit-scrollbar-thumb:hover {
        background-color: #a0a0a0;
    }
`;

const ModalHeaderRow = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 16px;
`;

const ModalTitleText = styled.div`
    font-family: Inter;
    font-size: 22px;
    font-weight: 600;
    line-height: 28px;
    letter-spacing: -0.44px;
    color: #151514;
`;

const ModalSubtitleText = styled.div`
    font-family: Inter;
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
    color: #909090;
    margin-top: 6px;
`;

const ModalCloseBtn = styled.button`
    background: none;
    border: none;
    cursor: pointer;
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    color: #151514;
    opacity: 0.5;
    transition: opacity 0.2s;
    &:hover { opacity: 1; }
`;

const RecurringRow = styled.label`
    display: flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;
    font-family: Inter;
    font-size: 15px;
    font-weight: 400;
    line-height: 20px;
    color: #151514;

    input[type='checkbox'] {
        width: 18px;
        height: 18px;
        accent-color: #151514;
        cursor: pointer;
        flex-shrink: 0;
    }
`;

const TwoColRow = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
`;

const ModalDatePicker = styled(StyledDateTimePicker)`
    width: 100%;
    .react-date-picker__wrapper,
    .react-datetime-picker__wrapper {
        background-color: #f2f2f2;
        width: 100%;
    }
`;

const TimeInputWrapper = styled(FlexBox)`
    position: relative;
    width: 100%;
    height: 48px;

    input[type='time'] {
        width: 100%;
        padding: 14px 44px 14px 16px;
        font-family: Inter;
        font-size: 15px;
        font-weight: 400;
        line-height: 20px;
        color: #151514;
        background-color: #f2f2f2;
        border: none;
        border-radius: 3px;
        outline: none;
        -webkit-appearance: none;
        appearance: none;
        box-sizing: border-box;

        &::-webkit-calendar-picker-indicator { display: none; }
    }
`;

const ClockIconButton = styled.button`
    position: absolute;
    right: 14px;
    top: 50%;
    transform: translateY(-50%);
    background: none;
    border: none;
    padding: 0;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
`;

const FullWidthLessonTypeWrapper = styled(LessonTypeWrapper)`
    width: 100%;
    height: 48px;

    .ui-input_container {
        height: 48px !important;
    }
`;

const LocationInput = styled.input`
    width: 100%;
    padding: 14px 16px;
    font-family: Inter;
    font-size: 15px;
    font-weight: 400;
    line-height: 20px;
    color: #151514;
    background-color: #f2f2f2;
    border: none;
    border-radius: 3px;
    outline: none;
    box-sizing: border-box;

    &::placeholder { color: #909090; }
`;

const startOfToday = (): Date => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
};

const addMonths = (date: Date, months: number): Date => {
    const next = new Date(date);
    next.setMonth(next.getMonth() + months);
    return next;
};

const truncateGroupLabel = (name: string): string =>
    name.length > MAX_GROUP_BUTTON_LABEL ? `${name.slice(0, MAX_GROUP_BUTTON_LABEL)}...` : name;

export const LessonCreateModal: FC<LessonCreateModalProps> = ({
    defaultStartDate,
    defaultEndDate,
    maxDate,
    courseGroups,
    lessonTypeOptions,
    getGroupName,
    onSubmit,
}) => {
    const { t, i18n } = useTranslation();
    const modal = useModal();
    const toasts = useToasts();
    const startTimeRef = useRef<HTMLInputElement | null>(null);
    const endTimeRef = useRef<HTMLInputElement | null>(null);

    const today = useMemo(startOfToday, []);
    const initialEndDate = useMemo(
        () => defaultEndDate ?? addMonths(defaultStartDate ?? today, 1),
        [defaultEndDate, defaultStartDate, today],
    );

    const { control, watch, setValue, handleSubmit } = useForm<LessonCreateFormValues>({
        mode: 'onSubmit',
        reValidateMode: 'onSubmit',
        shouldUnregister: false,
        defaultValues: {
            isRecurring: true,
            courseStartDate: defaultStartDate ?? today,
            courseEndDate: initialEndDate,
            singleDate: defaultStartDate ?? today,
            selectedDays: [],
            start: '',
            end: '',
            selectedLessonType: LMSLessonClassTypeModel.Ordinal,
            location: '',
            selectedGroups: [],
        },
    });

    const isRecurring = watch('isRecurring');
    const selectedDays = watch('selectedDays');
    const selectedGroups = watch('selectedGroups');
    const startTime = watch('start');

    const toggleInArray = useCallback(
        <T,>(arr: T[], item: T): T[] =>
            arr.includes(item) ? arr.filter((x) => x !== item) : [...arr, item],
        [],
    );

    const handleDayToggle = useCallback(
        (dayValue: number) => {
            setValue('selectedDays', toggleInArray(watch('selectedDays') || [], dayValue), {
                shouldDirty: false,
            });
        },
        [setValue, toggleInArray, watch],
    );

    const handleGroupToggle = useCallback(
        (groupId: string) => {
            setValue('selectedGroups', toggleInArray(watch('selectedGroups') || [], groupId), {
                shouldDirty: false,
            });
        },
        [setValue, toggleInArray, watch],
    );

    const handleLocalSubmit = useCallback(
        (data: LessonCreateFormValues) => {
            if (!data.start || !data.end) {
                toasts.error(t('lms.lessonsEditor.enterTimeError'));
                return;
            }

            if (data.isRecurring) {
                if (!data.courseStartDate) {
                    toasts.error(t('lms.lessonsEditor.enterStartDateError'));
                    return;
                }
                if (!data.courseEndDate) {
                    toasts.error(t('lms.lessonsEditor.enterEndDateError'));
                    return;
                }
                if (!data.selectedDays.length) {
                    toasts.error(t('lms.lessonsEditor.selectDayError'));
                    return;
                }
                onSubmit(
                    {
                        courseStartDate: data.courseStartDate,
                        courseEndDate: data.courseEndDate,
                        selectedDays: data.selectedDays,
                        start: data.start,
                        end: data.end,
                        selectedLessonType: data.selectedLessonType,
                        zoomLink: data.location,
                        selectedGroups: data.selectedGroups,
                    },
                    () => modal.hide(),
                );
                return;
            }

            if (!data.singleDate) {
                toasts.error(t('lms.lessonsEditor.enterStartDateError'));
                return;
            }
            onSubmit(
                {
                    courseStartDate: data.singleDate,
                    courseEndDate: data.singleDate,
                    selectedDays: [data.singleDate.getDay()],
                    start: data.start,
                    end: data.end,
                    selectedLessonType: data.selectedLessonType,
                    zoomLink: data.location,
                    selectedGroups: data.selectedGroups,
                },
                () => modal.hide(),
            );
        },
        [modal, onSubmit, t, toasts],
    );

    const weekDays = useMemo(
        () => [
            { label: t('lms.lessonsEditor.weekDays.mon'), value: 1 },
            { label: t('lms.lessonsEditor.weekDays.tue'), value: 2 },
            { label: t('lms.lessonsEditor.weekDays.wed'), value: 3 },
            { label: t('lms.lessonsEditor.weekDays.thu'), value: 4 },
            { label: t('lms.lessonsEditor.weekDays.fri'), value: 5 },
            { label: t('lms.lessonsEditor.weekDays.sat'), value: 6 },
            { label: t('lms.lessonsEditor.weekDays.sun'), value: 0 },
        ],
        [t],
    );

    const focusTimeInput = (ref: React.MutableRefObject<HTMLInputElement | null>) => () => {
        ref.current?.showPicker?.();
        ref.current?.focus();
    };

    return (
        <ModalWrapper>
            <ModalHeaderRow>
                <div>
                    <ModalTitleText>{t('lms.lessonsEditor.modalTitle')}</ModalTitleText>
                    <ModalSubtitleText>{t('lms.lessonsEditor.subtitle')}</ModalSubtitleText>
                </div>
                <ModalCloseBtn onClick={() => modal.hide()} aria-label="close">
                    <CloseIcon fill="currentColor" />
                </ModalCloseBtn>
            </ModalHeaderRow>

            <RecurringRow>
                <Controller
                    name="isRecurring"
                    control={control}
                    render={({ field }) => (
                        <input
                            type="checkbox"
                            checked={field.value}
                            onChange={(e) => field.onChange(e.target.checked)}
                        />
                    )}
                />
                {t('lms.lessonsEditor.recurringCheckbox')}
            </RecurringRow>

            {isRecurring ? (
                <>
                    <TwoColRow>
                        <FlexBox direction="column" gy={8}>
                            <CalendarLabel>{t('lms.lessonsEditor.startDateLabel')}</CalendarLabel>
                            <Controller
                                name="courseStartDate"
                                control={control}
                                render={({ field }) => (
                                    <ModalDatePicker
                                        value={field.value as unknown as Date}
                                        onChange={(date) => field.onChange(date as Date | null)}
                                        format="dd.MM.yyyy"
                                        calendarIcon={<Date24Icon />}
                                        clearIcon={null}
                                        locale={i18n.language}
                                        minDate={today}
                                        maxDate={maxDate ?? undefined}
                                    />
                                )}
                            />
                        </FlexBox>
                        <FlexBox direction="column" gy={8}>
                            <CalendarLabel>{t('lms.lessonsEditor.endDateLabel')}</CalendarLabel>
                            <Controller
                                name="courseEndDate"
                                control={control}
                                render={({ field }) => (
                                    <ModalDatePicker
                                        value={field.value as unknown as Date}
                                        onChange={(date) => field.onChange(date as Date | null)}
                                        format="dd.MM.yyyy"
                                        calendarIcon={<Date24Icon />}
                                        clearIcon={null}
                                        locale={i18n.language}
                                        minDate={watch('courseStartDate') || today}
                                        maxDate={maxDate ?? undefined}
                                    />
                                )}
                            />
                        </FlexBox>
                    </TwoColRow>

                    <FlexBox direction="column" gy={8}>
                        <CalendarLabel>{t('lms.lessonsEditor.selectDayLabel')}</CalendarLabel>
                        <DaysContainer style={{ height: 'auto', flexWrap: 'wrap' }}>
                            {weekDays.map((day) => (
                                <DayButton
                                    key={day.value}
                                    isSelected={selectedDays.includes(day.value)}
                                    onClick={() => handleDayToggle(day.value)}
                                >
                                    {day.label}
                                </DayButton>
                            ))}
                        </DaysContainer>
                    </FlexBox>
                </>
            ) : (
                <FlexBox direction="column" gy={8}>
                    <CalendarLabel>{t('lms.lessonsEditor.singleDateLabel')}</CalendarLabel>
                    <Controller
                        name="singleDate"
                        control={control}
                        render={({ field }) => (
                            <ModalDatePicker
                                value={field.value as unknown as Date}
                                onChange={(date) => field.onChange(date as Date | null)}
                                format="dd.MM.yyyy"
                                calendarIcon={<Date24Icon />}
                                clearIcon={null}
                                locale={i18n.language}
                                minDate={today}
                                maxDate={maxDate ?? undefined}
                            />
                        )}
                    />
                </FlexBox>
            )}

            <TwoColRow>
                <FlexBox direction="column" gy={8}>
                    <CalendarLabel>{t('lms.lessonsEditor.startTimeLabel')}</CalendarLabel>
                    <TimeInputWrapper>
                        <Controller
                            name="start"
                            control={control}
                            render={({ field }) => (
                                <input
                                    type="time"
                                    value={field.value}
                                    ref={(e) => {
                                        field.ref(e);
                                        startTimeRef.current = e;
                                    }}
                                    onChange={(e) => {
                                        const next = e.target.value;
                                        const end = watch('end');
                                        if (end && next && end < next) {
                                            setValue('end', '', { shouldDirty: false });
                                        }
                                        field.onChange(next);
                                    }}
                                />
                            )}
                        />
                        <ClockIconButton type="button" onClick={focusTimeInput(startTimeRef)} aria-label="pick start time">
                            <ClockIcon16 />
                        </ClockIconButton>
                    </TimeInputWrapper>
                </FlexBox>
                <FlexBox direction="column" gy={8}>
                    <CalendarLabel>{t('lms.lessonsEditor.endTimeLabel')}</CalendarLabel>
                    <TimeInputWrapper>
                        <Controller
                            name="end"
                            control={control}
                            render={({ field }) => (
                                <input
                                    type="time"
                                    value={field.value}
                                    min={startTime || undefined}
                                    ref={(e) => {
                                        field.ref(e);
                                        endTimeRef.current = e;
                                    }}
                                    onChange={(e) => {
                                        const next = e.target.value;
                                        if (startTime && next && next < startTime) {
                                            toasts.error(t('lms.lessonsEditor.endTimeBeforeStartError'));
                                            field.onChange('');
                                            return;
                                        }
                                        field.onChange(next);
                                    }}
                                />
                            )}
                        />
                        <ClockIconButton type="button" onClick={focusTimeInput(endTimeRef)} aria-label="pick end time">
                            <ClockIcon16 />
                        </ClockIconButton>
                    </TimeInputWrapper>
                </FlexBox>
            </TwoColRow>

            <TwoColRow>
                <FlexBox direction="column" gy={8}>
                    <CalendarLabel>{t('lms.lessonsEditor.lessonTypeLabel')}</CalendarLabel>
                    <FullWidthLessonTypeWrapper>
                        <Controller
                            name="selectedLessonType"
                            control={control}
                            render={({ field }) => (
                                <CommonInput
                                    type={INPUT_TYPES.dropdown}
                                    orientation="column"
                                    options={lessonTypeOptions as unknown as Record<string, string>[]}
                                    value={lessonTypeOptions.find((opt) => opt.value === String(field.value))}
                                    onChange={((option: LessonTypeOption | null) => {
                                        field.onChange(Number(option?.value ?? LMSLessonClassTypeModel.Ordinal));
                                    }) as never}
                                    placeholder={t('lms.lessonsEditor.selectLessonType')}
                                    dropdownPosition="top"
                                    maxHeight={240}
                                />
                            )}
                        />
                    </FullWidthLessonTypeWrapper>
                </FlexBox>
                <FlexBox direction="column" gy={8}>
                    <CalendarLabel>{t('lms.lessonsEditor.locationLabel')}</CalendarLabel>
                    <Controller
                        name="location"
                        control={control}
                        render={({ field }) => (
                            <LocationInput
                                type="text"
                                placeholder={t('lms.lessonsEditor.enterData')}
                                {...field}
                            />
                        )}
                    />
                </FlexBox>
            </TwoColRow>

            {courseGroups.length > 0 && (
                <FlexBox direction="column" gy={8}>
                    <CalendarLabel>{t('lms.lessonsEditor.groupsLabel')}</CalendarLabel>
                    <GroupsContainer style={{ maxWidth: '100%' }}>
                        <GroupsRow>
                            {courseGroups.map((group) => (
                                <GroupButton
                                    key={group.groupId}
                                    isSelected={selectedGroups.includes(group.groupId)}
                                    onClick={() => handleGroupToggle(group.groupId)}
                                >
                                    {truncateGroupLabel(getGroupName(group))}
                                </GroupButton>
                            ))}
                        </GroupsRow>
                    </GroupsContainer>
                </FlexBox>
            )}

            <StyledRectangularButtonFilled
                style={{ width: '100%' }}
                onClick={handleSubmit(handleLocalSubmit)}
            >
                {t('lms.lessonsEditor.addLessonButton')}
            </StyledRectangularButtonFilled>
        </ModalWrapper>
    );
};
import React, { FC, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';
import { CommonInput, FlexBox, INPUT_TYPES, useToasts } from '@brg/ui';
import styled from 'styled-components';
import { useModal } from 'core';
import {
    CalendarLabel,
    DayButton,
    DaysContainer,
    GroupButton,
    GroupsContainer,
    GroupsRow,
    LessonTypeWrapper,
    StyledDateTimePicker,
    StyledRectangularButtonFilled,
} from 'shared/pages/projectEdit/components/LMS/LessonsEditor/styles';
import {
    ILessonData,
    LMSLessonClassTypeModel,
} from 'shared/pages/projectEdit/components/LMS/LessonsEditor/LessonsEditorTypes';
import { Date24Icon } from 'shared/components/tagAdminProjectsSearchBar/date24';
import CloseIcon from 'shared/icons/CloseIcon';
import ClockIcon16 from 'shared/icons/ClockIcon16';

const MAX_GROUP_BUTTON_LABEL = 15;

interface CourseGroup {
    groupId: string;
    name: Array<{ lang: string; name: string }>;
}

interface LessonTypeOption {
    value: string;
    label: string;
}

interface LessonCreateFormValues {
    isRecurring: boolean;
    courseStartDate: Date | null;
    courseEndDate: Date | null;
    singleDate: Date | null;
    selectedDays: number[];
    start: string;
    end: string;
    selectedLessonType: LMSLessonClassTypeModel;
    location: string;
    selectedGroups: string[];
}

export interface LessonCreateModalProps {
    defaultStartDate?: Date | null;
    defaultEndDate?: Date | null;
    maxDate?: Date | null;
    courseGroups: CourseGroup[];
    lessonTypeOptions: LessonTypeOption[];
    getGroupName: (group: CourseGroup) => string;
    onSubmit: (data: ILessonData, onSuccess?: () => void) => void;
}

const ModalWrapper = styled.div`
    background: #ffffff;
    border-radius: 8px;
    width: 720px;
    max-width: calc(100vw - 32px);
    padding: 32px;
    display: flex;
    flex-direction: column;
    gap: 20px;
    max-height: 90vh;
    overflow-y: auto;
    scrollbar-width: thin;
    scrollbar-color: #c4c4c4 transparent;

    &::-webkit-scrollbar {
        width: 6px;
    }
    &::-webkit-scrollbar-track {
        background: transparent;
    }
    &::-webkit-scrollbar-thumb {
        background-color: #c4c4c4;
        border-radius: 3px;
    }
    &::-webkit-scrollbar-thumb:hover {
        background-color: #a0a0a0;
    }
`;

const ModalHeaderRow = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 16px;
`;

const ModalTitleText = styled.div`
    font-family: Inter;
    font-size: 22px;
    font-weight: 600;
    line-height: 28px;
    letter-spacing: -0.44px;
    color: #151514;
`;

const ModalSubtitleText = styled.div`
    font-family: Inter;
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
    color: #909090;
    margin-top: 6px;
`;

const ModalCloseBtn = styled.button`
    background: none;
    border: none;
    cursor: pointer;
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    color: #151514;
    opacity: 0.5;
    transition: opacity 0.2s;
    &:hover { opacity: 1; }
`;

const RecurringRow = styled.label`
    display: flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;
    font-family: Inter;
    font-size: 15px;
    font-weight: 400;
    line-height: 20px;
    color: #151514;

    input[type='checkbox'] {
        width: 18px;
        height: 18px;
        accent-color: #151514;
        cursor: pointer;
        flex-shrink: 0;
    }
`;

const TwoColRow = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
`;

const ModalDatePicker = styled(StyledDateTimePicker)`
    width: 100%;
    .react-date-picker__wrapper,
    .react-datetime-picker__wrapper {
        background-color: #f2f2f2;
        width: 100%;
    }
`;

const TimeInputWrapper = styled(FlexBox)`
    position: relative;
    width: 100%;
    height: 48px;

    input[type='time'] {
        width: 100%;
        padding: 14px 44px 14px 16px;
        font-family: Inter;
        font-size: 15px;
        font-weight: 400;
        line-height: 20px;
        color: #151514;
        background-color: #f2f2f2;
        border: none;
        border-radius: 3px;
        outline: none;
        -webkit-appearance: none;
        appearance: none;
        box-sizing: border-box;

        &::-webkit-calendar-picker-indicator { display: none; }
    }
`;

const ClockIconButton = styled.button`
    position: absolute;
    right: 14px;
    top: 50%;
    transform: translateY(-50%);
    background: none;
    border: none;
    padding: 0;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
`;

const FullWidthLessonTypeWrapper = styled(LessonTypeWrapper)`
    width: 100%;
    height: 48px;

    .ui-input_container {
        height: 48px !important;
    }
`;

const LocationInput = styled.input`
    width: 100%;
    padding: 14px 16px;
    font-family: Inter;
    font-size: 15px;
    font-weight: 400;
    line-height: 20px;
    color: #151514;
    background-color: #f2f2f2;
    border: none;
    border-radius: 3px;
    outline: none;
    box-sizing: border-box;

    &::placeholder { color: #909090; }
`;

const startOfToday = (): Date => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
};

const addMonths = (date: Date, months: number): Date => {
    const next = new Date(date);
    next.setMonth(next.getMonth() + months);
    return next;
};

const truncateGroupLabel = (name: string): string =>
    name.length > MAX_GROUP_BUTTON_LABEL ? `${name.slice(0, MAX_GROUP_BUTTON_LABEL)}...` : name;

export const LessonCreateModal: FC<LessonCreateModalProps> = ({
    defaultStartDate,
    defaultEndDate,
    maxDate,
    courseGroups,
    lessonTypeOptions,
    getGroupName,
    onSubmit,
}) => {
    const { t, i18n } = useTranslation();
    const modal = useModal();
    const toasts = useToasts();
    const startTimeRef = useRef<HTMLInputElement | null>(null);
    const endTimeRef = useRef<HTMLInputElement | null>(null);

    const today = useMemo(startOfToday, []);
    const initialEndDate = useMemo(
        () => defaultEndDate ?? addMonths(defaultStartDate ?? today, 1),
        [defaultEndDate, defaultStartDate, today],
    );

    const { control, watch, setValue, handleSubmit } = useForm<LessonCreateFormValues>({
        mode: 'onSubmit',
        reValidateMode: 'onSubmit',
        shouldUnregister: false,
        defaultValues: {
            isRecurring: true,
            courseStartDate: defaultStartDate ?? today,
            courseEndDate: initialEndDate,
            singleDate: defaultStartDate ?? today,
            selectedDays: [],
            start: '',
            end: '',
            selectedLessonType: LMSLessonClassTypeModel.Ordinal,
            location: '',
            selectedGroups: [],
        },
    });

    const isRecurring = watch('isRecurring');
    const selectedDays = watch('selectedDays');
    const selectedGroups = watch('selectedGroups');
    const startTime = watch('start');

    const toggleInArray = useCallback(
        <T,>(arr: T[], item: T): T[] =>
            arr.includes(item) ? arr.filter((x) => x !== item) : [...arr, item],
        [],
    );

    const handleDayToggle = useCallback(
        (dayValue: number) => {
            setValue('selectedDays', toggleInArray(watch('selectedDays') || [], dayValue), {
                shouldDirty: false,
            });
        },
        [setValue, toggleInArray, watch],
    );

    const handleGroupToggle = useCallback(
        (groupId: string) => {
            setValue('selectedGroups', toggleInArray(watch('selectedGroups') || [], groupId), {
                shouldDirty: false,
            });
        },
        [setValue, toggleInArray, watch],
    );

    const handleLocalSubmit = useCallback(
        (data: LessonCreateFormValues) => {
            if (!data.start || !data.end) {
                toasts.error(t('lms.lessonsEditor.enterTimeError'));
                return;
            }

            if (data.isRecurring) {
                if (!data.courseStartDate) {
                    toasts.error(t('lms.lessonsEditor.enterStartDateError'));
                    return;
                }
                if (!data.courseEndDate) {
                    toasts.error(t('lms.lessonsEditor.enterEndDateError'));
                    return;
                }
                if (!data.selectedDays.length) {
                    toasts.error(t('lms.lessonsEditor.selectDayError'));
                    return;
                }
                onSubmit(
                    {
                        courseStartDate: data.courseStartDate,
                        courseEndDate: data.courseEndDate,
                        selectedDays: data.selectedDays,
                        start: data.start,
                        end: data.end,
                        selectedLessonType: data.selectedLessonType,
                        zoomLink: data.location,
                        selectedGroups: data.selectedGroups,
                    },
                    () => modal.hide(),
                );
                return;
            }

            if (!data.singleDate) {
                toasts.error(t('lms.lessonsEditor.enterStartDateError'));
                return;
            }
            onSubmit(
                {
                    courseStartDate: data.singleDate,
                    courseEndDate: data.singleDate,
                    selectedDays: [data.singleDate.getDay()],
                    start: data.start,
                    end: data.end,
                    selectedLessonType: data.selectedLessonType,
                    zoomLink: data.location,
                    selectedGroups: data.selectedGroups,
                },
                () => modal.hide(),
            );
        },
        [modal, onSubmit, t, toasts],
    );

    const weekDays = useMemo(
        () => [
            { label: t('lms.lessonsEditor.weekDays.mon'), value: 1 },
            { label: t('lms.lessonsEditor.weekDays.tue'), value: 2 },
            { label: t('lms.lessonsEditor.weekDays.wed'), value: 3 },
            { label: t('lms.lessonsEditor.weekDays.thu'), value: 4 },
            { label: t('lms.lessonsEditor.weekDays.fri'), value: 5 },
            { label: t('lms.lessonsEditor.weekDays.sat'), value: 6 },
            { label: t('lms.lessonsEditor.weekDays.sun'), value: 0 },
        ],
        [t],
    );

    const focusTimeInput = (ref: React.MutableRefObject<HTMLInputElement | null>) => () => {
        ref.current?.showPicker?.();
        ref.current?.focus();
    };

    return (
        <ModalWrapper>
            <ModalHeaderRow>
                <div>
                    <ModalTitleText>{t('lms.lessonsEditor.modalTitle')}</ModalTitleText>
                    <ModalSubtitleText>{t('lms.lessonsEditor.subtitle')}</ModalSubtitleText>
                </div>
                <ModalCloseBtn onClick={() => modal.hide()} aria-label="close">
                    <CloseIcon fill="currentColor" />
                </ModalCloseBtn>
            </ModalHeaderRow>

            <RecurringRow>
                <Controller
                    name="isRecurring"
                    control={control}
                    render={({ field }) => (
                        <input
                            type="checkbox"
                            checked={field.value}
                            onChange={(e) => field.onChange(e.target.checked)}
                        />
                    )}
                />
                {t('lms.lessonsEditor.recurringCheckbox')}
            </RecurringRow>

            {isRecurring ? (
                <>
                    <TwoColRow>
                        <FlexBox direction="column" gy={8}>
                            <CalendarLabel>{t('lms.lessonsEditor.startDateLabel')}</CalendarLabel>
                            <Controller
                                name="courseStartDate"
                                control={control}
                                render={({ field }) => (
                                    <ModalDatePicker
                                        value={field.value as unknown as Date}
                                        onChange={(date) => field.onChange(date as Date | null)}
                                        format="dd.MM.yyyy"
                                        calendarIcon={<Date24Icon />}
                                        clearIcon={null}
                                        locale={i18n.language}
                                        minDate={today}
                                        maxDate={maxDate ?? undefined}
                                    />
                                )}
                            />
                        </FlexBox>
                        <FlexBox direction="column" gy={8}>
                            <CalendarLabel>{t('lms.lessonsEditor.endDateLabel')}</CalendarLabel>
                            <Controller
                                name="courseEndDate"
                                control={control}
                                render={({ field }) => (
                                    <ModalDatePicker
                                        value={field.value as unknown as Date}
                                        onChange={(date) => field.onChange(date as Date | null)}
                                        format="dd.MM.yyyy"
                                        calendarIcon={<Date24Icon />}
                                        clearIcon={null}
                                        locale={i18n.language}
                                        minDate={watch('courseStartDate') || today}
                                        maxDate={maxDate ?? undefined}
                                    />
                                )}
                            />
                        </FlexBox>
                    </TwoColRow>

                    <FlexBox direction="column" gy={8}>
                        <CalendarLabel>{t('lms.lessonsEditor.selectDayLabel')}</CalendarLabel>
                        <DaysContainer style={{ height: 'auto', flexWrap: 'wrap' }}>
                            {weekDays.map((day) => (
                                <DayButton
                                    key={day.value}
                                    isSelected={selectedDays.includes(day.value)}
                                    onClick={() => handleDayToggle(day.value)}
                                >
                                    {day.label}
                                </DayButton>
                            ))}
                        </DaysContainer>
                    </FlexBox>
                </>
            ) : (
                <FlexBox direction="column" gy={8}>
                    <CalendarLabel>{t('lms.lessonsEditor.singleDateLabel')}</CalendarLabel>
                    <Controller
                        name="singleDate"
                        control={control}
                        render={({ field }) => (
                            <ModalDatePicker
                                value={field.value as unknown as Date}
                                onChange={(date) => field.onChange(date as Date | null)}
                                format="dd.MM.yyyy"
                                calendarIcon={<Date24Icon />}
                                clearIcon={null}
                                locale={i18n.language}
                                minDate={today}
                                maxDate={maxDate ?? undefined}
                            />
                        )}
                    />
                </FlexBox>
            )}

            <TwoColRow>
                <FlexBox direction="column" gy={8}>
                    <CalendarLabel>{t('lms.lessonsEditor.startTimeLabel')}</CalendarLabel>
                    <TimeInputWrapper>
                        <Controller
                            name="start"
                            control={control}
                            render={({ field }) => (
                                <input
                                    type="time"
                                    value={field.value}
                                    ref={(e) => {
                                        field.ref(e);
                                        startTimeRef.current = e;
                                    }}
                                    onChange={(e) => {
                                        const next = e.target.value;
                                        const end = watch('end');
                                        if (end && next && end < next) {
                                            setValue('end', '', { shouldDirty: false });
                                        }
                                        field.onChange(next);
                                    }}
                                />
                            )}
                        />
                        <ClockIconButton type="button" onClick={focusTimeInput(startTimeRef)} aria-label="pick start time">
                            <ClockIcon16 />
                        </ClockIconButton>
                    </TimeInputWrapper>
                </FlexBox>
                <FlexBox direction="column" gy={8}>
                    <CalendarLabel>{t('lms.lessonsEditor.endTimeLabel')}</CalendarLabel>
                    <TimeInputWrapper>
                        <Controller
                            name="end"
                            control={control}
                            render={({ field }) => (
                                <input
                                    type="time"
                                    value={field.value}
                                    min={startTime || undefined}
                                    ref={(e) => {
                                        field.ref(e);
                                        endTimeRef.current = e;
                                    }}
                                    onChange={(e) => {
                                        const next = e.target.value;
                                        if (startTime && next && next < startTime) {
                                            toasts.error(t('lms.lessonsEditor.endTimeBeforeStartError'));
                                            field.onChange('');
                                            return;
                                        }
                                        field.onChange(next);
                                    }}
                                />
                            )}
                        />
                        <ClockIconButton type="button" onClick={focusTimeInput(endTimeRef)} aria-label="pick end time">
                            <ClockIcon16 />
                        </ClockIconButton>
                    </TimeInputWrapper>
                </FlexBox>
            </TwoColRow>

            <TwoColRow>
                <FlexBox direction="column" gy={8}>
                    <CalendarLabel>{t('lms.lessonsEditor.lessonTypeLabel')}</CalendarLabel>
                    <FullWidthLessonTypeWrapper>
                        <Controller
                            name="selectedLessonType"
                            control={control}
                            render={({ field }) => (
                                <CommonInput
                                    type={INPUT_TYPES.dropdown}
                                    orientation="column"
                                    options={lessonTypeOptions as unknown as Record<string, string>[]}
                                    value={lessonTypeOptions.find((opt) => opt.value === String(field.value))}
                                    onChange={((option: LessonTypeOption | null) => {
                                        field.onChange(Number(option?.value ?? LMSLessonClassTypeModel.Ordinal));
                                    }) as never}
                                    placeholder={t('lms.lessonsEditor.selectLessonType')}
                                    dropdownPosition="top"
                                    maxHeight={240}
                                />
                            )}
                        />
                    </FullWidthLessonTypeWrapper>
                </FlexBox>
                <FlexBox direction="column" gy={8}>
                    <CalendarLabel>{t('lms.lessonsEditor.locationLabel')}</CalendarLabel>
                    <Controller
                        name="location"
                        control={control}
                        render={({ field }) => (
                            <LocationInput
                                type="text"
                                placeholder={t('lms.lessonsEditor.enterData')}
                                {...field}
                            />
                        )}
                    />
                </FlexBox>
            </TwoColRow>

            {courseGroups.length > 0 && (
                <FlexBox direction="column" gy={8}>
                    <CalendarLabel>{t('lms.lessonsEditor.groupsLabel')}</CalendarLabel>
                    <GroupsContainer style={{ maxWidth: '100%' }}>
                        <GroupsRow>
                            {courseGroups.map((group) => (
                                <GroupButton
                                    key={group.groupId}
                                    isSelected={selectedGroups.includes(group.groupId)}
                                    onClick={() => handleGroupToggle(group.groupId)}
                                >
                                    {truncateGroupLabel(getGroupName(group))}
                                </GroupButton>
                            ))}
                        </GroupsRow>
                    </GroupsContainer>
                </FlexBox>
            )}

            <StyledRectangularButtonFilled
                style={{ width: '100%' }}
                onClick={handleSubmit(handleLocalSubmit)}
            >
                {t('lms.lessonsEditor.addLessonButton')}
            </StyledRectangularButtonFilled>
        </ModalWrapper>
    );
};
